import type { FlagKey, UserRole } from '@doneverse/shared'
import { isNavItemVisible, panelNavItems } from '@/components/layout/founderNavConfig'
import { isAllowedForRestrictedDoer } from '@/lib/doerAccess'
import { MESSAGING_SURFACE_ROLES } from '@/lib/messaging-access'

/**
 * Where Coach Grace is allowed to take the user, and how the agent refers to it.
 *
 * The list is derived from `panelNavItems` — the same registry the sidebar
 * renders — rather than being a hand-written copy. That is the whole point: a
 * page the founder's own menu hides (wrong role, flag off) must not be a page
 * the coach can open on their behalf, and a page added to the menu should
 * become navigable without anyone remembering to update a second list.
 */
export interface CoachGraceDestination {
  /** Stable key the agent passes back in the tool call, derived from the path. */
  page: string
  /** Human name — the label the founder sees in their own menu. */
  label: string
  /** Router path to navigate to, for a Dashboard page. */
  to?: string
  /**
   * Absolute URL, for a menu link that leaves the Dashboard (Refer a Friend).
   * Opened in a new tab, the way the sidebar opens it, so the conversation
   * survives. Only ever a doneverse.com URL — see {@link isDoneverseUrl}.
   */
  href?: string
}

/**
 * Whether a URL is ours: https on doneverse.com or one of its subdomains.
 *
 * doneverse.com links are the ones Grace may open for a founder. Everything else
 * a menu item links to (the Zoom room) stays out of her reach. Compared on the
 * parsed hostname, never by prefix, so `doneverse.com.example.net` is not ours.
 */
export function isDoneverseUrl(url: string): boolean {
  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return false
  }
  if (parsed.protocol !== 'https:') return false
  return parsed.hostname === 'doneverse.com' || parsed.hostname.endsWith('.doneverse.com')
}

/**
 * Surfaces a founder uses that have no sidebar entry, so `panelNavItems` can't
 * supply them. Messages earns its place explicitly: "message your Doer from the
 * Dashboard" is in Grace's guardrails, and it was the one destination she was
 * told to point at but had no way to open.
 */
const OFF_MENU_DESTINATIONS: { to: string; label: string; roles?: readonly UserRole[] }[] = [
  { to: '/messages', label: 'Messages', roles: MESSAGING_SURFACE_ROLES },
  { to: '/notifications', label: 'Notifications' },
  { to: '/settings', label: 'Settings' },
]

/**
 * Lowercase alphanumerics, single-spaced — so "Doing Playbooks",
 * "doing-playbooks" and "/doing playbooks" all compare on equal terms. The
 * agent speaks in labels and thinks in keys; matching has to accept both.
 */
function normalize(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

function pageKey(to: string): string {
  return to.replace(/^\//, '')
}

/** Whole-word containment on normalized text: "launch checklist" holds "checklist", not "check". */
function containsPhrase(haystack: string, needle: string): boolean {
  return ` ${haystack} `.includes(` ${needle} `)
}

/**
 * The pages this user may be taken to, in menu order.
 *
 * `restrictedDoer` mirrors the sidebar's own argument (see `lib/doerAccess`).
 * The widget is founder-only today, so it is always false there — it is threaded
 * through anyway so widening the widget's audience can't silently hand a
 * founder-less doer a route their menu and router both refuse.
 */
export function buildCoachGraceDestinations(
  role: string | undefined,
  hasFlag: (flag: FlagKey) => boolean,
  restrictedDoer = false,
): CoachGraceDestination[] {
  const byPath = new Map<string, CoachGraceDestination>()

  for (const item of panelNavItems) {
    if (!isNavItemVisible(item, role, hasFlag, restrictedDoer)) continue

    // External `href` items open a new tab rather than routing. A doneverse.com
    // one (Refer a Friend) is still ours to open: leaving it out is what made
    // Grace tell a founder the page did not exist and guess Settings instead.
    if (!item.to) {
      if (!item.href || !isDoneverseUrl(item.href) || byPath.has(item.href)) continue
      byPath.set(item.href, {
        page: normalize(item.label).replace(/ /g, '-'),
        label: item.label,
        href: item.href,
      })
      continue
    }
    // Several paths appear twice with a role-specific label (/doer-profile is
    // "Your Doer" for a founder, "Your Profile" for a doer). Role filtering
    // above leaves one, but /groups is listed for founders and admins alike —
    // first wins, which is the label this role's sidebar would render.
    if (byPath.has(item.to)) continue
    byPath.set(item.to, { page: pageKey(item.to), label: item.label, to: item.to })
  }

  for (const item of OFF_MENU_DESTINATIONS) {
    if (item.roles && !item.roles.includes(role as UserRole)) continue
    if (restrictedDoer && !isAllowedForRestrictedDoer(item.to)) continue
    if (byPath.has(item.to)) continue
    byPath.set(item.to, { page: pageKey(item.to), label: item.label, to: item.to })
  }

  return [...byPath.values()]
}

/**
 * The founder's own playbooks as destinations, so "open my Lead Magnet playbook"
 * lands on that playbook's page instead of the board. Built from workspace data
 * on demand rather than published up-front: the agent already learns playbook
 * names from its own tools, and subscribing the widget to the workspace query on
 * every page just to name them would cost a fetch nobody reads.
 */
export function buildPlaybookDestinations(
  playbooks: readonly { playbook?: { slug?: string | null; name?: string | null } | null }[],
): CoachGraceDestination[] {
  const byPath = new Map<string, CoachGraceDestination>()

  for (const { playbook } of playbooks) {
    const slug = playbook?.slug
    if (!slug) continue
    const to = `/playbooks/${slug}`
    if (byPath.has(to)) continue
    byPath.set(to, { page: slug, label: playbook?.name || slug, to })
  }

  return [...byPath.values()]
}

/**
 * Why a request didn't land on exactly one page. `ambiguous` is kept separate
 * from `none` because they need opposite answers: "that doesn't exist" sends
 * the coach looking for a different page, when what she should do is ask the
 * founder which of two real ones they meant.
 */
export type DestinationMatch =
  | { status: 'found'; destination: CoachGraceDestination }
  | { status: 'ambiguous'; candidates: CoachGraceDestination[] }
  | { status: 'none' }

/**
 * An exact key or label hit.
 *
 * Split out because it is the one match safe to act on without seeing every
 * other destination: an exact name can't turn out to be a near-miss for
 * something else once a wider set is in view. Callers use it as a fast path so
 * the common request never has to go and load the founder's playbooks.
 */
export function findExactDestination(
  destinations: readonly CoachGraceDestination[],
  requested: string,
): CoachGraceDestination | null {
  const wanted = normalize(requested)
  if (!wanted) return null
  return destinations.find((d) => normalize(d.page) === wanted || normalize(d.label) === wanted) ?? null
}

/**
 * Match what the agent asked for against what it may open.
 *
 * Anything short of an exact name must be judged against EVERY destination the
 * founder could have meant — menu pages and their own playbooks in one set.
 * Deciding within the menu alone looks like it works and quietly doesn't:
 * "launch" is a unique menu hit for Launch Checklist, so a founder who owns the
 * `New Online Course Launch` playbook gets sent to the checklist with no sign
 * anything was ambiguous.
 */
export function resolveCoachGraceDestination(
  destinations: readonly CoachGraceDestination[],
  requested: string,
): DestinationMatch {
  const exact = findExactDestination(destinations, requested)
  if (exact) return { status: 'found', destination: exact }

  const wanted = normalize(requested)
  if (!wanted) return { status: 'none' }

  // Both directions: "checklist" for "Launch Checklist", and "my tasks page"
  // for "Tasks". A request naming several pages also lands here, and is refused
  // for the same reason a genuine collision is.
  const partial = destinations.filter((d) => {
    const label = normalize(d.label)
    const page = normalize(d.page)
    return (
      containsPhrase(label, wanted) ||
      containsPhrase(page, wanted) ||
      containsPhrase(wanted, label) ||
      containsPhrase(wanted, page)
    )
  })

  if (partial.length === 1) return { status: 'found', destination: partial[0] }
  if (partial.length > 1) return { status: 'ambiguous', candidates: partial }
  return { status: 'none' }
}

/** What the tool tells the agent when it asked for a page that doesn't exist. */
export function describeDestinations(destinations: readonly CoachGraceDestination[]): string {
  return destinations.map((d) => d.label).join(', ')
}
